import { Component, type ErrorInfo, type ReactNode } from "react";
import { BRAND } from "@/lib/themes";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary] Uncaught render error:", error, info);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleHome = () => {
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        data-testid="error-boundary-fallback"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "1.5rem",
          backgroundColor: BRAND.offWhite,
          color: BRAND.charcoal,
          fontFamily:
            "'DM Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
          WebkitFontSmoothing: "antialiased",
        }}
      >
        <div
          style={{
            maxWidth: "32rem",
            width: "100%",
            backgroundColor: "#FFFFFF",
            border: "1px solid #E8E2D6",
            borderRadius: "1.5rem",
            boxShadow: "0 24px 60px -20px rgba(31, 27, 22, 0.18)",
            padding: "2.5rem 2rem",
            textAlign: "center",
          }}
        >
          <p
            style={{
              color: "var(--brand-sage)",
              fontSize: "0.7rem",
              fontWeight: 700,
              letterSpacing: "0.24em",
              textTransform: "uppercase",
              marginBottom: "1rem",
            }}
          >
            Something Broke
          </p>
          <h1
            style={{
              fontSize: "1.875rem",
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              fontWeight: 700,
              marginBottom: "0.875rem",
              color: BRAND.charcoal,
            }}
          >
            We hit an unexpected snag.
          </h1>
          <p
            style={{
              marginBottom: "1.75rem",
              lineHeight: 1.55,
              color: "#3F3A33",
              fontSize: "1rem",
            }}
          >
            The page ran into an error and couldn&rsquo;t finish loading. Try
            refreshing — that fixes it most of the time.
          </p>
          <div
            style={{
              display: "flex",
              gap: "0.625rem",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              onClick={this.handleReload}
              data-testid="button-error-reload"
              style={{
                fontFamily: "inherit",
                fontWeight: 600,
                fontSize: "0.95rem",
                padding: "0.75rem 1.5rem",
                backgroundColor: BRAND.charcoal,
                color: BRAND.offWhite,
                border: "none",
                borderRadius: "9999px",
                cursor: "pointer",
              }}
            >
              Refresh page
            </button>
            <button
              type="button"
              onClick={this.handleHome}
              data-testid="button-error-home"
              style={{
                fontFamily: "inherit",
                fontWeight: 600,
                fontSize: "0.95rem",
                padding: "0.75rem 1.5rem",
                backgroundColor: "#FFFFFF",
                color: BRAND.charcoal,
                border: "1px solid #E8E2D6",
                borderRadius: "9999px",
                cursor: "pointer",
              }}
            >
              Go home
            </button>
          </div>
          {import.meta.env.DEV && this.state.error && (
            <pre
              style={{
                marginTop: "1.75rem",
                textAlign: "left",
                fontSize: "0.72rem",
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                backgroundColor: BRAND.charcoal,
                color: BRAND.offWhite,
                padding: "1rem",
                borderRadius: "0.75rem",
                overflow: "auto",
                maxHeight: "12rem",
                whiteSpace: "pre-wrap",
              }}
            >
              {this.state.error.message}
              {"\n"}
              {this.state.error.stack}
            </pre>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
